import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  User, Phone, Mail, LogOut, ChevronRight, Shield, 
  Bell, HelpCircle, FileText, Loader2, Building2, Car,
  ShoppingBag, TrendingUp, PiggyBank, Landmark, Zap
} from 'lucide-react';

const menuItems = [
  { icon: ShoppingBag, label: 'Shop', href: '/buy', color: 'from-cyan-500 to-teal-500' },
  { icon: Building2, label: 'Homes', href: '/homes', color: 'from-green-500 to-emerald-500' },
  { icon: Car, label: 'Auto', href: '/auto', color: 'from-yellow-500 to-amber-500' },
  { icon: TrendingUp, label: 'Markets', href: '/markets', color: 'from-orange-500 to-red-500' },
  { icon: PiggyBank, label: 'Wealth', href: '/wealth', color: 'from-cyan-500 to-blue-500' },
  { icon: Landmark, label: 'Capital', href: '/capital', color: 'from-purple-500 to-pink-500' },
];

const settingsItems = [
  { icon: Bell, label: 'Notifications', href: '#' },
  { icon: Shield, label: 'Security & Privacy', href: '#' },
  { icon: HelpCircle, label: 'Help Center', href: '#' },
  { icon: FileText, label: 'Terms & Conditions', href: '#' },
];

export default function ProfilePage() {
  const { user, logout, updateProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile(name, email);
      toast.success('Profile updated successfully');
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout title="Profile">
      <div className="p-4 lg:p-6 space-y-6">
        {/* Profile Card */}
        <Card data-testid="profile-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 via-green-500 to-orange-500 flex items-center justify-center">
                <User className="h-10 w-10 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold">{user?.name || 'Jambo User'}</h2>
                <p className="text-muted-foreground">{user?.phone}</p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => setEditing(!editing)}
                className="rounded-full"
                data-testid="edit-profile-btn"
              >
                {editing ? 'Cancel' : 'Edit'}
              </Button>
            </div>

            {editing ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Name</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="profile-email-input"
                  />
                </div>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="w-full rounded-full jambo-gradient text-white"
                  data-testid="save-profile-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save Changes
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                  <Phone className="h-5 w-5 text-cyan-500" />
                  <span>{user?.phone || 'Not set'}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                  <Mail className="h-5 w-5 text-green-500" />
                  <span>{user?.email || 'Not set'}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Invest Status */}
        <Card className="invest-gradient border-0">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-orange-500 flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-medium">Quick Invest</p>
                <p className="text-xs text-muted-foreground">Round-up investing is active</p>
              </div>
            </div>
            <Button size="sm" variant="outline" className="rounded-full">
              Manage
            </Button>
          </CardContent>
        </Card>

        {/* Services */}
        <Card>
          <CardHeader>
            <CardTitle>Services</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {menuItems.map((item) => (
                <Link 
                  key={item.label} 
                  to={item.href}
                  className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                  data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center`}>
                      <item.icon className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-medium">{item.label}</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Settings</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {settingsItems.map((item) => (
                <Link 
                  key={item.label} 
                  to={item.href}
                  className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                  data-testid={`setting-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="h-5 w-5 text-muted-foreground" />
                    <span>{item.label}</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Logout */}
        <Button 
          variant="destructive" 
          className="w-full rounded-full"
          onClick={logout}
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5 mr-2" />
          Logout
        </Button>

        {/* Version */}
        <p className="text-center text-xs text-muted-foreground">
          Version 1.0.0 • myjambo.co
        </p>
      </div>
    </AppLayout>
  );
}
